import { Link, useRouterState } from '@tanstack/react-router'

import { HomeIcon } from '@gto/icons-react/outline/home'
import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import { useMe } from '@/auth/me'
import { type AdminNavItem, canViewSection, sectionForPath, visibleNavItems } from '@/auth/sections'

/**
 * The item whose path is the LONGEST prefix of the current one, or null when the
 * group has no match at all.
 *
 * Prefix (not exact) matching is what keeps a parent item lit while the operator
 * is on a deep route below it — `/gamemodes/17700/upload` must still light "Game
 * modes", and `/announcements/17` must still light "All announcements", neither of
 * which has a nav item of its own. But a plain "every item that prefixes the path"
 * rule lights TWO items the moment a group gains a nested sibling: on
 * `/announcements/help` both "All announcements" and "How to" match. Longest wins
 * resolves that without giving up the deep-route behaviour — an exact match would
 * fix the collision by breaking every deep route instead.
 *
 * Matching is on a segment boundary, so a future `/announcements-archive` cannot
 * light "All announcements" by sharing a string prefix mid-segment.
 */
const findActiveItem = (items: readonly AdminNavItem[], pathname: string): AdminNavItem | null => {
  let best: AdminNavItem | null = null

  for (const item of items) {
    const matches = pathname === item.to || pathname.startsWith(`${item.to}/`)
    if (matches && (best === null || item.to.length > best.to.length)) {
      best = item
    }
  }

  return best
}

type AdminNavItemProps = {
  item: AdminNavItem
  isActive: boolean
}

const AdminNavItem = ({ item, isActive }: AdminNavItemProps) => {
  return (
    <Link
      // TanStack's own active match is fuzzy by default, so on `/announcements/help`
      // it would ALSO claim "All announcements" as the current page and stamp its
      // active props onto that link. `exact` stops it double-claiming; the highlight
      // and the `aria-current` below stay driven by `findActiveItem`, whose
      // longest-prefix rule is what keeps a parent item lit on a deep route.
      activeOptions={{ exact: true }}
      aria-current={isActive ? 'page' : undefined}
      data-tst={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      to={item.to}
      className={cn(
        'flex rounded-md px-3 py-2 no-underline transition-colors',
        isActive ? 'bg-primary-main/10 text-primary-main' : 'text-text-primary hover:bg-white/5',
      )}
    >
      <Typography variant="body2">{item.label}</Typography>
    </Link>
  )
}

export const AdminNav = () => {
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  const me = useMe()
  const section = sectionForPath(pathname)
  const activeGroup =
    section === undefined || !canViewSection(me, section)
      ? undefined
      : { heading: section.heading, items: visibleNavItems(me, section) }
  const activeItem = activeGroup === undefined ? null : findActiveItem(activeGroup.items, pathname)

  return (
    <nav
      aria-label="Admin"
      className="flex flex-col gap-3"
      data-tst="admin-nav"
    >
      <Link
        className="flex flex-row items-center gap-2 rounded-md px-3 py-2 text-text-secondary no-underline transition-colors hover:bg-white/5 hover:text-text-primary"
        data-tst="admin-nav-home"
        to="/"
      >
        <HomeIcon className="size-4 shrink-0" />
        <Typography variant="body2">Admin home</Typography>
      </Link>

      {activeGroup !== undefined && (
        <div className="flex flex-col gap-0.5">
          <Typography
            className="px-3"
            color="text.secondary"
            variant="overline"
          >
            {activeGroup.heading}
          </Typography>
          {activeGroup.items.map((item) => (
            <AdminNavItem
              key={item.to}
              isActive={item === activeItem}
              item={item}
            />
          ))}
        </div>
      )}
    </nav>
  )
}
